import { Directive, OnDestroy, OnInit } from '@angular/core';
import { AdminService, ToolbarStateAdmin, ToolbarTypeAdmin } from '../admin.service';
import {
    IBase,
    IFaq,
    IFilter,
    IGlossary,
    IHyperlink,
    IReport,
    IResponsible,
    IUser,
    ResponsibleType
} from '@shared/interfaces';
import { Subscription } from 'rxjs';
import { DialogService } from '@shared/services';
import { IPaginatedResult } from '@shared/interfaces/mailing/IPaginatedResult';
import { ModuleService } from '@shared/services/module.service';
import { ModuleDirective } from '@shared/directives/module.directive';

@Directive()
export class AdminActionsDirective extends ModuleDirective implements OnInit, OnDestroy {
    subscription: Subscription;
    previousQuery: ResponsibleType;

    constructor(
        public adminService: AdminService,
        public dialogService: DialogService,
        moduleService: ModuleService
    ) {
        super(moduleService);
    }

    set data(val: IBase[]) {
        this.adminService.data = val;
    }

    get data(): IBase[] {
        return this.adminService.data ?? this.paginatedData?.items;
    }

    get _data(): IBase[] {
        return this.adminService._data ?? this._paginatedData?.items;
    }

    get paginatedData(): IPaginatedResult<IBase> {
        return this.adminService.paginatedData;
    }

    set paginatedData(val: IPaginatedResult<IBase>) {
        this.adminService.paginatedData = val;
    }

    get _paginatedData(): IPaginatedResult<IBase> {
        return this.adminService._paginatedData;
    }

    ngOnInit() {
        this.subscription = this.adminService.changeFilter.subscribe(e => this.applyFilter(e));
    }

    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }

    protected disableSubmitter(event: SubmitEvent): void {
        const submitter: HTMLButtonElement =
            (event.submitter as HTMLButtonElement) || (event.target as HTMLButtonElement);

        if (submitter) {
            submitter.disabled = true;
        }
    }

    protected enableSubmitter(event: SubmitEvent): void {
        const submitter: HTMLButtonElement =
            (event.submitter as HTMLButtonElement) || (event.target as HTMLButtonElement);

        if (submitter) {
            submitter.disabled = false;
        }
    }

    changeFilter(event: IFilter | string) {
        this.adminService.changeFilter.next(event);
    }

    protected applyFilter(_event: string | IFilter): void {
        /* Handler */
    }

    get sortActive(): boolean {
        return this.adminService.sortActive;
    }

    get selectedItem(): IBase {
        return this.adminService.selectedItem;
    }

    set selectedItem(val: IBase) {
        this.adminService.selectedItem = val;
    }

    get toolbarState(): ToolbarStateAdmin {
        return this.adminService.toolbarState;
    }

    get toolbarType(): ToolbarTypeAdmin {
        return this.adminService.toolbarType;
    }

    get isCreateView(): boolean {
        return this.toolbarState === ToolbarStateAdmin.Create;
    }

    get isStandardView(): boolean {
        return this.toolbarState === ToolbarStateAdmin.Standard;
    }

    get isEditView(): boolean {
        return this.toolbarState === ToolbarStateAdmin.Edit;
    }

    get isInfoView(): boolean {
        return this.toolbarState === ToolbarStateAdmin.Info;
    }

    get isFaqView(): boolean {
        return this.toolbarType === ToolbarTypeAdmin.Faq;
    }

    get isFaqStandard(): boolean {
        return this.isStandardView && this.isFaqView;
    }

    get isFaqCreate(): boolean {
        return this.isCreateView && this.isFaqView;
    }

    get isFaqEdit(): boolean {
        return this.isEditView && this.isFaqView;
    }

    get isUserView(): boolean {
        return this.toolbarType === ToolbarTypeAdmin.Users;
    }

    get isUserStandard(): boolean {
        return this.isStandardView && this.isUserView;
    }

    get isUserInfo(): boolean {
        return this.isInfoView && this.isUserView;
    }

    get isUserCreate(): boolean {
        return this.isCreateView && this.isUserView;
    }

    get isUserEdit(): boolean {
        return this.isEditView && this.isUserView;
    }

    get isResponsibleView(): boolean {
        return this.toolbarType === ToolbarTypeAdmin.Responsibilities;
    }

    get isResponsibleStandard(): boolean {
        return this.isStandardView && this.isResponsibleView;
    }

    get isResponsibleInfo(): boolean {
        return this.isInfoView && this.isResponsibleView;
    }

    get isHyperlinkView(): boolean {
        return this.toolbarType === ToolbarTypeAdmin.Hyperlinks;
    }

    get isGlossaryView(): boolean {
        return this.toolbarType === ToolbarTypeAdmin.Glossary;
    }

    get isReportView(): boolean {
        return this.toolbarType === ToolbarTypeAdmin.Reports;
    }

    get isReportTemplateView(): boolean {
        return this.toolbarType === ToolbarTypeAdmin.ReportTemplates;
    }

    goToMainView() {
        this.adminService.toolbarChanged.next({ state: ToolbarStateAdmin.Standard, type: ToolbarTypeAdmin.Users });
    }

    goToResponsibilities(responsibleType?: ResponsibleType) {
        this.previousQuery = responsibleType;
        this.adminService.toolbarChanged.next({
            state: ToolbarStateAdmin.Standard,
            type: ToolbarTypeAdmin.Responsibilities,
            query: responsibleType
        });
    }

    goToReports() {
        this.adminService.toolbarChanged.next({ state: ToolbarStateAdmin.Standard, type: ToolbarTypeAdmin.Reports });
    }

    goToReportTemplates() {
        this.adminService.toolbarChanged.next({
            state: ToolbarStateAdmin.Standard,
            type: ToolbarTypeAdmin.ReportTemplates
        });
    }

    goToHyperlinks() {
        this.adminService.toolbarChanged.next({ state: ToolbarStateAdmin.Standard, type: ToolbarTypeAdmin.Hyperlinks });
    }

    goToGlossary() {
        this.adminService.toolbarChanged.next({ state: ToolbarStateAdmin.Standard, type: ToolbarTypeAdmin.Glossary });
    }

    goToFaq() {
        this.adminService.toolbarChanged.next({ state: ToolbarStateAdmin.Standard, type: ToolbarTypeAdmin.Faq });
    }

    goBackInFaq(item?: IFaq, items?: IFaq[]) {
        this.adminService.toolbarChanged.next({
            state: ToolbarStateAdmin.Standard,
            type: ToolbarTypeAdmin.Faq,
            selectedItem: item?.id ? item : null,
            items,
            noReload: !!items
        });
    }

    editFaqItem(item: IFaq) {
        this.adminService.toolbarChanged.next({
            state: ToolbarStateAdmin.Edit,
            type: ToolbarTypeAdmin.Faq,
            selectedItem: item
        });
    }

    startSort(item: IFaq) {
        this.adminService.toolbarChanged.next({
            state: ToolbarStateAdmin.Standard,
            type: ToolbarTypeAdmin.Faq,
            selectedItem: item,
            sort: true,
            noReload: true
        });
    }

    addFaq() {
        this.adminService.toolbarChanged.next({ state: ToolbarStateAdmin.Create, type: ToolbarTypeAdmin.Faq });
    }

    goToUser(selectedItem: IUser) {
        this.setScrollTop();
        this.adminService.toolbarChanged.next({
            state: ToolbarStateAdmin.Info,
            type: ToolbarTypeAdmin.Users,
            selectedItem
        });
    }

    editUser(selectedItem: IUser) {
        this.adminService.toolbarChanged.next({
            state: ToolbarStateAdmin.Edit,
            type: ToolbarTypeAdmin.Users,
            selectedItem
        });
    }

    createUser() {
        this.setScrollTop();
        this.adminService.toolbarChanged.next({ state: ToolbarStateAdmin.Create, type: ToolbarTypeAdmin.Users });
    }

    goToResponsible(selectedItem: IResponsible) {
        this.setScrollTop();
        this.adminService.toolbarChanged.next({
            state: ToolbarStateAdmin.Info,
            type: ToolbarTypeAdmin.Responsibilities,
            selectedItem
        });
    }

    createResponsible() {
        this.setScrollTop();
        this.adminService.toolbarChanged.next({
            state: ToolbarStateAdmin.Create,
            type: ToolbarTypeAdmin.Responsibilities
        });
    }

    editResponsible(selectedItem: IResponsible) {
        this.adminService.toolbarChanged.next({
            state: ToolbarStateAdmin.Edit,
            type: ToolbarTypeAdmin.Responsibilities,
            selectedItem
        });
    }

    setScrollTop() {
        if (document.getElementById('itemContainer')) {
            this.adminService.scrollTop = document.getElementById('itemContainer').scrollTop;
        }
    }

    goToHyperlink(selectedItem: IHyperlink) {
        this.setScrollTop();
        this.adminService.toolbarChanged.next({
            state: ToolbarStateAdmin.Info,
            type: ToolbarTypeAdmin.Hyperlinks,
            selectedItem
        });
    }

    createHyperlink() {
        this.setScrollTop();
        this.adminService.toolbarChanged.next({ state: ToolbarStateAdmin.Create, type: ToolbarTypeAdmin.Hyperlinks });
    }

    editHyperlink(selectedItem: IHyperlink) {
        this.adminService.toolbarChanged.next({
            state: ToolbarStateAdmin.Edit,
            type: ToolbarTypeAdmin.Hyperlinks,
            selectedItem
        });
    }

    goToGlossaryItem(selectedItem: IGlossary) {
        this.setScrollTop();
        this.adminService.toolbarChanged.next({
            state: ToolbarStateAdmin.Info,
            type: ToolbarTypeAdmin.Glossary,
            selectedItem
        });
    }

    createGlossary() {
        this.setScrollTop();
        this.adminService.toolbarChanged.next({ state: ToolbarStateAdmin.Create, type: ToolbarTypeAdmin.Glossary });
    }

    editGlossary(selectedItem: IGlossary) {
        this.adminService.toolbarChanged.next({
            state: ToolbarStateAdmin.Edit,
            type: ToolbarTypeAdmin.Glossary,
            selectedItem
        });
    }

    createReport() {
        this.adminService.toolbarChanged.next({ state: ToolbarStateAdmin.Create, type: ToolbarTypeAdmin.Reports });
    }

    editReport(selectedItem: IReport) {
        this.adminService.toolbarChanged.next({
            state: ToolbarStateAdmin.Edit,
            type: ToolbarTypeAdmin.Reports,
            selectedItem
        });
    }

    deleteDialog(): Promise<boolean> {
        return this.dialogService.deleteDialog();
    }

    async resetDialog(): Promise<boolean> {
        return this.dialogService
            .openDialog({
                title: 'dialog.generic.resetQuestion',
                message: 'dialog.generic.resetQuestionDesc',
                sureQuestion: true
            })
            .toPromise()
            .then(e => e?.result ?? false);
    }
}
